import { useEffect, useState } from "react";
import { Button, Modal, Typography } from "@/components/atoms";
import { clsx } from "@/utils";
import { TourBlockContent } from "./TourBlocks";
import { TourSlide } from "./types";

interface AnnouncementTourProps {
  open: boolean;
  slides: TourSlide[];
  /**
   * Fires ONLY from the last slide's primary button. There is no other way out — see the Modal below.
   */
  onClose: () => void;
  /** Label for the last slide's button. */
  ctaLabel?: string;
}

/**
 * A dismiss-once announcement carousel: purple hero, content blocks, dot pagination, Back/Next.
 *
 * Content-driven (see `types.ts`) so a future announcement is a slide array rather than a new modal.
 * It knows nothing about who should see it or whether they already have — that belongs to the caller,
 * because the eligibility rule differs per campaign while this chrome does not.
 */
export const AnnouncementTour = ({ open, slides, onClose, ctaLabel = "Get Started" }: AnnouncementTourProps) => {
  const [index, setIndex] = useState(0);

  // Reopening should start at the beginning. Cheap insurance: today the tour is once-per-user so this
  // never fires in production, but a re-openable campaign (a "what's new" menu item) would otherwise
  // resume mid-deck with no way back to slide one.
  useEffect(() => {
    if (open) setIndex(0);
  }, [open]);

  if (slides.length === 0) return null;

  // Clamped rather than indexed directly: a campaign edited down to fewer slides while someone has the
  // modal open would otherwise read past the end and crash the portal on an announcement.
  const slide = slides[Math.min(index, slides.length - 1)];
  const isFirst = index === 0;
  const isLast = index >= slides.length - 1;

  const handleNext = () => (isLast ? onClose() : setIndex((current) => current + 1));

  /*
   * No dismiss route but the last slide's primary button: `undefined` rather than a handler, because
   * Modal already defaults onClose to a no-op, so backdrop and Escape do nothing on every slide.
   *
   * Design's call — the deck is meant to be read through rather than clicked past. Not a trap: Next is
   * always available and always reaches the last slide, where the button closes it.
   *
   * The 550px cap is the design width: with the panel's px-6 off each side it leaves the 502px content
   * column the video card is specced against.
   */
  return (
    <Modal open={open} onClose={undefined} variant="web-medium" className="max-w-[550px] overflow-hidden p-0">
      <div className="flex w-full flex-col">
        {/* Full-bleed hero. The panel's own p-6 is overridden above so this reaches the rounded edges;
            the flat purple stands in for the designer's photograph.
            UNRESOLVED: this purple is fixed while the dots and numbered badges below use `bg-primary`,
            which is `var(--color-primary)` — per-operator branding. They agree only for operators whose
            brand is purple. Either the hero follows primary too, or those accents should be fixed. */}
        <div className="flex flex-col items-start gap-4 bg-vivid-purple-dark px-6 py-7">
          <span className="w-fit rounded-md border border-neutral-mid-gray bg-white px-2 py-1">
            <Typography variant="small" className="text-neutral-black">
              {slide.eyebrow}
            </Typography>
          </span>
          <Typography variant="h2" className="text-white">
            {slide.title}
          </Typography>
        </div>

        {/* Capped so a long slide scrolls inside the modal rather than pushing the footer off a laptop
            screen — the Next button must stay reachable at every viewport. */}
        <div className="flex max-h-[60vh] flex-col gap-4 overflow-y-auto px-6 py-5">
          {slide.blocks.map((block, blockIndex) => (
            <TourBlockContent key={`${block.type}-${blockIndex}`} block={block} />
          ))}
          {/* Inset rather than full width: centred small print reads badly across the whole 502px
              column, and letting it wrap on its own measure survives copy edits, which pinning the
              break to a particular word would not. */}
          {slide.footnote && (
            <Typography variant="paragraph" className="px-8 text-center text-neutral-dark-gray">
              {slide.footnote}
            </Typography>
          )}
        </div>

        <div className="flex items-center justify-between gap-4 px-6 pb-6 pt-2">
          <ol className="flex items-center gap-1.5" aria-label={`Slide ${index + 1} of ${slides.length}`}>
            {slides.map((each, dotIndex) => (
              <li
                key={each.title}
                aria-current={dotIndex === index ? "step" : undefined}
                className={clsx(
                  "h-1.5 rounded-full transition-all duration-200",
                  dotIndex === index ? "w-5 bg-primary" : "w-1.5 bg-neutral-mid-gray"
                )}
              />
            ))}
          </ol>

          <div className="flex items-center gap-3">
            {!isFirst && (
              <Button variant="tertiary" size="lg" onClick={() => setIndex((current) => current - 1)}>
                Back
              </Button>
            )}
            <Button variant="primary" size="lg" onClick={handleNext}>
              {isLast ? ctaLabel : "Next"}
            </Button>
          </div>
        </div>
      </div>
    </Modal>
  );
};
